import { memo, useCallback } from 'react';

import { type CardSize } from '@air/api/types';
import { SegmentedControl, type SegmentedControlOption } from '@air/component-segmented-control';

import { useCardSizePreference } from '~/hooks/useCardSizePreference';

const CARD_SIZE_OPTIONS: Record<CardSize, SegmentedControlOption> = {
  small: {
    id: 'small',
    label: 'Small',
    testId: 'VIEW_CARD_SIZE_OPTION_SMALL',
    tooltip: 'Small',
    value: 'small',
  },
  medium: {
    id: 'medium',
    label: 'Medium',
    testId: 'VIEW_CARD_SIZE_OPTION_MEDIUM',
    tooltip: 'Medium',
    value: 'medium',
  },
  large: {
    id: 'large',
    label: 'Large',
    testId: 'VIEW_CARD_SIZE_OPTION_LARGE',
    tooltip: 'Large',
    value: 'large',
  },
  'extra-large': {
    id: 'extra-large',
    label: 'Extra Large',
    testId: 'VIEW_CARD_SIZE_OPTION_EXTRA_LARGE',
    tooltip: 'Extra Large',
    value: 'extra-large',
  },
};

const CARD_SIZE_ORDER: CardSize[] = ['small', 'medium', 'large', 'extra-large'];

const CARD_SIZE_OPTION_LIST = CARD_SIZE_ORDER.map((size) => CARD_SIZE_OPTIONS[size]);

const isCardSize = (value: string): value is CardSize => value in CARD_SIZE_OPTIONS;

export const ViewCardSizePreference = memo(() => {
  const { cardSize, updateCardSize } = useCardSizePreference();
  // Stored sizes are unvalidated localStorage/saved-view values, and the segmented control throws on an unknown option.
  const selectedCardSizeOption = isCardSize(cardSize) ? CARD_SIZE_OPTIONS[cardSize] : CARD_SIZE_OPTIONS.medium;

  const handleSelect = useCallback(
    (option: SegmentedControlOption) => {
      // Options only ever come from CARD_SIZE_OPTIONS, so the guard narrows rather than filters.
      if (isCardSize(option.id)) {
        updateCardSize(option.id);
      }
    },
    [updateCardSize],
  );

  return (
    <div className="flex flex-col gap-1.5 px-1.5" data-testid="VIEW_CARD_SIZE">
      <span className="font-medium ads:text-primary text-14 text-gray-12">Size</span>
      {/* Not fullWidth: four segments sharing the popover would leave "Extra Large" narrower than its label. */}
      <SegmentedControl
        appearance="rectangle"
        color="gray"
        onSelect={handleSelect}
        options={CARD_SIZE_OPTION_LIST}
        outerAppearance="default"
        selectedOption={selectedCardSizeOption}
        size="xsmall"
      />
    </div>
  );
});

ViewCardSizePreference.displayName = 'ViewCardSizePreference';
